import { useFlowStore } from '@/state/stores/use-flow-store';
import { useEventStore } from '@/state/stores/use-event-store';
import Item from './item';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import { dateToMinutesFromMidnight } from '@/helpers/date.helper';
import { useTranslations } from '@/i18n/use-translations';
import { useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  pointerWithin,
  rectIntersection,
  type CollisionDetection,
  type DragStartEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FlowDto, FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import computeAllSlotTimings, { calculateFlowDurationFromSlots } from './calculate';
import { Box, Divider, Stack } from '@mui/material';
import { useScreenSize } from '@/hooks/use-screen-size';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useParams, useSearchParams } from 'react-router';
import InlineEndTimingForm from './inline-end-timing-form';
import { useNotifications } from '@/hooks/use-notifications';
import { useFlowMode } from '../use-flow-mode';
import { isEntityOfMode, isOrganizeMode } from '@/components/events/helpers/mode.helper';
import { useModeStore } from '@/state/stores/use-mode-store';
import InlineEndLabelForm from './inline-end-label-form';
import EditOutlined from '@mui/icons-material/EditOutlined';
import { IconButton } from '@mui/material';
import { useSlotListDnd } from './use-slot-list-dnd';
import { useSlotListComputed } from './use-slot-list-computed';
import { useSlotListState } from './use-slot-list-state';
import { useSlotListForm } from './use-slot-list-form';
import { UntimedSection } from './untimed-section';
import { TimedSection } from './timed-section';
import { useSlotListSync } from './use-slot-list-sync';
import { useFlowSyncTransport } from '@/state/mutations/flows/use-flow-sync-transport';
import { findUpdatedSlotForSelection } from '../flow-slot-selection-match';
import { FLOW_CONTROL_SLOT_LIST_GAP, type FlowDetailMode } from '../flow-detail.types';
import { slotListScrollAreaSx } from '../flow-detail-tabs/flow-slot-list-column-sx';
import { flowControlInlinePerformMiddleWrapperSx } from '../flow-control-perform-expand';
import type { Mode } from '@/types/entity-mode';
import { FLOW_CONTROL_PERFORM_EXPAND_FLOW } from '../flow-control-perform-expand';
import type { ScheduleInlineDetailsTab } from '../edit-details/schedule-inline-details-tab';
import SlotAssetsDetails from '../edit-details/slot-assets-details';
import {
  FLOW_CONTROL_AFTER_FLOW_PLAYOUT_ID,
  FLOW_CONTROL_PRE_FLOW_PLAYOUT_ID,
  FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID,
  FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID,
} from './flow-slot-assets-inline-ids';
import { FlowControlSlotMediaBelowRow } from './flow-control-slot-media-below-row';
import { FlowControlPlayoutSegmentRow } from './flow-control-playout-segment-row';
import { useFlowControlVirtualInBetweenPlayout } from './use-flow-control-virtual-in-between-playout';
import { useFlowControlPlayingSegmentExpandId } from './use-flow-control-playing-segment-expand-id';
import { useScheduleLiveSlotLock } from './use-schedule-live-slot-lock';
import type { ScheduleLiveTimedSlotLock } from '@/helpers/schedule-live-slot-lock.helper';
import { parseInBetweenPlayoutSegmentId } from '@/helpers/flow-control-in-between-playout.helper';
import { useFlowControlMediaExpandStore } from '@/state/stores/use-flow-control-media-expand-store';
import { FlowEndSlotAssetsDetails } from '../edit-details/flow-end-slot-assets-details';
import { buildFlowSyncEmitPayload } from './build-flow-sync-emit-payload';
import { normalizeFlowForPlanView } from '../normalize-flow-plan-view';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import { mergeFlowPreservingLiveContext } from '@/helpers/merge-flow-preserving-live-context.helper';
import { Publish } from '@/components/events/detail/tabs/flows/grid/actions/publish';
import CollapsibleInfoAlert from '@/components/common/collapsible-info-alert';
import { useFlowPublishStateForFlowDetail } from '@/components/events/detail/tabs/flows/grid/actions/use-flow-publish-state-for-flow-detail';
import { slotListInfoTitleKeyFromPublishState } from '@/components/events/detail/tabs/flows/grid/actions/slot-list-info-title';
import Actions from './actions';

type DragOverlaySlotProps = {
  activeId: string | null;
  timedSlots: FlowSlotDto[];
  untimedSlots: FlowSlotDto[];
  selectedSlotId?: string;
  onSlotUpdate: (slot: FlowSlotDto) => void;
  onPreviewSlotUpdate: (slot: FlowSlotDto) => void;
  onShowTimingAlert?: () => void;
  cancelChanges?: () => void;
  onSetEditingTiming: (editing: boolean, slotId: string) => void;
  forceTimedPreview?: boolean;
  hideTime?: boolean;
  rightSectionOnlyPublishIcon?: boolean;
  listFlowStartOffset?: number;
};

const isUntimedSlot = (slot: FlowSlotDto) =>
  slot.isUntimed ?? (slot.plannedOffsetMinutes === undefined && slot.computedOffsetMinutes === undefined);

function buildLocalSlotsFromFlow(flow: FlowDto): FlowSlotDto[] {
  const timed = flow.slots.filter((slot) => slot.contentType !== 'Gap' && !isUntimedSlot(slot));
  const untimed = flow.slots.filter((slot) => slot.contentType !== 'Gap' && isUntimedSlot(slot));
  const flowDuration = flow.durationPlannedMinutes ?? calculateFlowDurationFromSlots(timed);
  const computedSlots = computeAllSlotTimings(timed, flowDuration);
  return [...computedSlots, ...untimed];
}

const DragOverlaySlot = ({
  activeId,
  timedSlots,
  untimedSlots,
  selectedSlotId,
  onSlotUpdate,
  onPreviewSlotUpdate,
  onShowTimingAlert,
  cancelChanges,
  onSetEditingTiming,
  forceTimedPreview = false,
  hideTime = false,
  rightSectionOnlyPublishIcon = false,
  listFlowStartOffset,
}: DragOverlaySlotProps) => {
  if (!activeId) return null;
  const draggedSlot = [...timedSlots, ...untimedSlots].find((slot) => slot.id === activeId);
  if (!draggedSlot) return null;
  const isDraggedSlotUntimed = isUntimedSlot(draggedSlot);

  return (
    <Item
      slot={draggedSlot}
      selected={selectedSlotId === draggedSlot.id}
      refreshing={false}
      locked={draggedSlot?.plannedOffsetMinutes !== undefined}
      shortenedBy={
        draggedSlot.plannedDurationMinutes !== undefined && draggedSlot.computedDurationMinutes !== undefined
          ? draggedSlot.plannedDurationMinutes - draggedSlot.computedDurationMinutes
          : 0
      }
      offsetViolation={false}
      gapAfter={0}
      onSlotUpdate={onSlotUpdate}
      onPreviewSlotUpdate={onPreviewSlotUpdate}
      onShowTimingAlert={onShowTimingAlert}
      cancelChanges={cancelChanges}
      setEditingTiming={(editing) => onSetEditingTiming(editing, draggedSlot.id)}
      isEditingTiming={false}
      isUntimed={isDraggedSlotUntimed}
      forceTimedPreview={forceTimedPreview}
      hideTime={hideTime}
      rightSectionOnlyPublishIcon={rightSectionOnlyPublishIcon}
      listFlowStartOffset={listFlowStartOffset}
      showSlotDragHandle={false}
    />
  );
};

type ListProps = {
  setShowFlowDetails: React.Dispatch<React.SetStateAction<FlowDetailMode | undefined>>;
  setFlowDetailsDirection: React.Dispatch<React.SetStateAction<'up' | 'down' | undefined>>;
  onSlotSelect?: (slot: FlowSlotDto, isUserInitiated?: boolean) => void;
  onShowTimingAlert?: () => void;
  onShowPublishAlert?: () => void;
  /** When set, overrides useFlowMode() so the slot list displays data for this mode (e.g. ORGANIZE for perform view) */
  modeOverride?: Mode;
  /** When true, disables editing, drag-drop, and add-slot forms (e.g. for organize column on large screens) */
  readOnly?: boolean;
  /** When true, right section shows only publish icon (flow schedule tab). When false, shows contributor/media icons (flow slot assets tab) */
  rightSectionOnlyPublishIcon?: boolean;
  /** When true, clicking middle section on small screens opens overlay (e.g. Slot Assets tab) */
  middleSectionOpensOverlayOnSmallScreen?: boolean;
  /** Start row: open this mode instead of flowSettings (flowControl on Control tab) */
  startRowMediaOpenDetailsMode?: FlowDetailMode;
  /** When set (e.g. flow schedule tab), wide-screen inline slot delete uses this instead of switching to flow settings */
  onWideScreenSlotDeletedFromList?: () => void;
  onSessionPublishSuccess?: () => void;
  /** Narrow Control tab: inline PerformMiddleSection under expanded slot / flow row */
  flowControlInlinePerform?: boolean;
  flowControlPerformExpandedKey?: string | null;
  onFlowControlPerformExpand?: (key: string | null) => void;
  renderFlowControlPerformMiddle?: (slot: FlowSlotDto) => React.ReactNode;
  renderFlowControlPerformMiddleFlow?: () => React.ReactNode;
  onFlowControlStartItemMiddleClick?: () => void;
  /** Narrow Slot Assets vs narrow Schedule: which inline panel under the slot row */
  smallScreenInlineDetailsVariant?: 'slotAssets' | 'schedule';
  /** When true, no horizontal rule between untimed and timed slot blocks */
  hideTimedUntimedSectionDivider?: boolean;
  /** When true, omit the top "flow start" row (time / duration / event slot day). Only `FlowScheduleTab` sets this. */
  hideFlowStartSlotRow?: boolean;
  /**
   * When true with `useScreenSize` `isSmallScreen`, omit the flow-start row. Only schedule-tab entry points set this
   * (see `SCHEDULE_SLOT_LIST_HIDE_FLOW_START_ON_SMALL_SCREEN` in `flow-schedule-tab.tsx`).
   */
  hideFlowStartSlotRowWhenSmallScreen?: boolean;
  /** When true, the end-of-flow row omits the media indicator (schedule tab only; see `flow-schedule-tab.tsx`). */
  hideEndItemMediaIndicator?: boolean;
  /** When true, draggable slots use a left-column drag handle; right column narrows (schedule tab only). */
  showSlotDragHandle?: boolean;
  /** When true, the slot-list info alert is omitted (parent used to render its own; prefer false so alert + title actions stay in List). */
  hideCollapsibleInfoAlert?: boolean;
  /** Schedule tab: borderless info alert without expand chevron (see `flow-schedule-tab.tsx`). */
  compactInfoAlert?: boolean;
  /**
   * When true, only the slot list body (DndContext) scrolls; publication info + actions stay above the scrollport
   * (narrow flow schedule column — same idea as `FlowSlotAssetsSlotListColumn`).
   */
  slotListBodyInScrollContainer?: boolean;
  flowSlotAssetsWideAnchor?: import('../flow-detail-tabs/flow-slot-assets-wide-anchor').FlowSlotAssetsWideAnchor;
  onFlowSlotAssetsWideAnchorChange?: (
    anchor: import('../flow-detail-tabs/flow-slot-assets-wide-anchor').FlowSlotAssetsWideAnchor,
  ) => void;
  /** Slot assets column: hide untimed block when empty (draft + published), without changing schedule tab PLAN behavior */
  hideEmptyUntimedSection?: boolean;
  /** Control tab: click-to-expand draft media under rows (both breakpoints) */
  flowControlInlineMedia?: boolean;
  /** Control tab: play icon stub in slot right section */
  rightSectionPlayIcon?: boolean;
};

const noop = () => {};

export default function List({
  setShowFlowDetails,
  setFlowDetailsDirection,
  onSlotSelect,
  onShowTimingAlert,
  onShowPublishAlert,
  modeOverride,
  readOnly = false,
  rightSectionOnlyPublishIcon = false,
  middleSectionOpensOverlayOnSmallScreen = false,
  startRowMediaOpenDetailsMode,
  onWideScreenSlotDeletedFromList,
  onSessionPublishSuccess,
  flowControlInlinePerform = false,
  flowControlPerformExpandedKey = null,
  onFlowControlPerformExpand,
  renderFlowControlPerformMiddle,
  renderFlowControlPerformMiddleFlow,
  onFlowControlStartItemMiddleClick,
  smallScreenInlineDetailsVariant,
  hideTimedUntimedSectionDivider = false,
  hideFlowStartSlotRow = false,
  hideFlowStartSlotRowWhenSmallScreen = false,
  hideEndItemMediaIndicator = false,
  showSlotDragHandle = false,
  hideCollapsibleInfoAlert = false,
  compactInfoAlert = false,
  slotListBodyInScrollContainer = false,
  flowSlotAssetsWideAnchor = 'start',
  onFlowSlotAssetsWideAnchorChange,
  hideEmptyUntimedSection = false,
  flowControlInlineMedia = false,
  rightSectionPlayIcon = false,
}: ListProps) {
  const { flowId } = useParams();
  const selectedSlot = useFlowStore((s) => s.selectedSlot);
  const setSelectedSlot = useFlowStore((s) => s.setSelectedSlot);
  const setFlow = useFlowStore((s) => s.setFlow);
  const setFlowPlan = useFlowStore((s) => s.setFlowPlan);
  const setFlowOrganize = useFlowStore((s) => s.setFlowOrganize);
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const flowOrganize = useFlowStore((s) => s.flowOrganize);
  const flow = useFlowStore((s) => s.flow);
  const t = useTranslations();
  const [isSyncing, setIsSyncing] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const notification = useNotifications();
  const flowModeResolved = useFlowMode();
  const mode = modeOverride ?? flowModeResolved;
  const hideGapSlotWizardAndBreak =
    readOnly && (startRowMediaOpenDetailsMode === 'flowSlotAssets' || startRowMediaOpenDetailsMode === 'flowControl');
  // When modeOverride is set, use the matching mode document for list rows (independent of global flow mode)
  const flowToUse = mode === 'ORGANIZE' && flowOrganize ? flowOrganize : mode === 'PLAN' && flowPlan ? flowPlan : flow;
  const queryClient = useQueryClient();
  const { isSmallScreen } = useScreenSize({ breakpoint: 860 }); // Match isWideScreen threshold
  /** Narrow Slot Assets: inline panel under the slot (same content as overlay) */
  const [slotAssetsInlineDetailsSlotId, setSlotAssetsInlineDetailsSlotId] = useState<string | null>(null);
  /** Control tab: which row shows inline draft media (both breakpoints) */
  const flowControlInlineMediaSlotId = useFlowControlMediaExpandStore((s) => s.expandId);
  const toggleFlowControlInlineMediaExpand = useFlowControlMediaExpandStore((s) => s.toggleExpandId);
  /** When the flow slot-list info alert is expanded, hide publish/NEW aside so the panel can use full width */
  const [slotListInfoAlertExpanded, setSlotListInfoAlertExpanded] = useState(false);
  const handleSlotAssetsInlineDetailsToggle = useCallback((slotId: string) => {
    setSlotAssetsInlineDetailsSlotId((prev) => (prev === slotId ? null : slotId));
  }, []);
  /** Collapse inline details when closing expanded row from middle click (toggle off without re-opening). */
  const handleSlotAssetsInlineDetailsClose = useCallback((slotId: string) => {
    setSlotAssetsInlineDetailsSlotId((prev) => (prev === slotId ? null : prev));
  }, []);
  const handleFlowControlInlineMediaToggle = useCallback(
    (slotId: string) => {
      toggleFlowControlInlineMediaExpand(slotId);
    },
    [toggleFlowControlInlineMediaExpand],
  );
  const expandSource = useFlowControlMediaExpandStore((s) => s.expandSource);
  const virtualInBetweenPlayout = useFlowControlVirtualInBetweenPlayout();
  const playingExpandId = useFlowControlPlayingSegmentExpandId();
  useEffect(() => {
    if (!flowControlInlineMedia || !flowControlInlineMediaSlotId) {
      return;
    }
    if (expandSource === 'manual') {
      return;
    }
    if (!parseInBetweenPlayoutSegmentId(flowControlInlineMediaSlotId)) {
      return;
    }
    if (playingExpandId === flowControlInlineMediaSlotId) {
      return;
    }
    if (!virtualInBetweenPlayout || virtualInBetweenPlayout.segmentId !== flowControlInlineMediaSlotId) {
      useFlowControlMediaExpandStore.getState().setExpandId(null);
    }
  }, [expandSource, flowControlInlineMedia, flowControlInlineMediaSlotId, playingExpandId, virtualInBetweenPlayout]);
  /** Narrow slot assets: flow start row opens moderators + flow media inline (same as wide SlotAssetsDetails with no slot). */
  const handleFlowStartSlotAssetsInlinePress = useCallback(() => {
    handleSlotAssetsInlineDetailsToggle(FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID);
    setSelectedSlot(undefined);
  }, [handleSlotAssetsInlineDetailsToggle, setSelectedSlot]);
  const handleFlowEndSlotAssetsPress = useCallback(() => {
    setSelectedSlot(undefined);
    if (isSmallScreen && middleSectionOpensOverlayOnSmallScreen) {
      handleSlotAssetsInlineDetailsToggle(FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID);
      return;
    }
    if (!isSmallScreen && startRowMediaOpenDetailsMode === 'flowSlotAssets' && onFlowSlotAssetsWideAnchorChange) {
      onFlowSlotAssetsWideAnchorChange('end');
      setSlotAssetsInlineDetailsSlotId(null);
    }
  }, [
    setSelectedSlot,
    isSmallScreen,
    middleSectionOpensOverlayOnSmallScreen,
    handleSlotAssetsInlineDetailsToggle,
    startRowMediaOpenDetailsMode,
    onFlowSlotAssetsWideAnchorChange,
  ]);
  /** Flow start row: list chrome when flow-level details are active (slot assets / flow control). */
  const isEndRowFlowLevelDetailsSelected = useMemo(() => {
    if (startRowMediaOpenDetailsMode !== 'flowSlotAssets') {
      return false;
    }
    if (!isSmallScreen) {
      return !selectedSlot && flowSlotAssetsWideAnchor === 'end';
    }
    return (
      Boolean(middleSectionOpensOverlayOnSmallScreen) &&
      slotAssetsInlineDetailsSlotId === FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID
    );
  }, [
    startRowMediaOpenDetailsMode,
    isSmallScreen,
    selectedSlot,
    flowSlotAssetsWideAnchor,
    middleSectionOpensOverlayOnSmallScreen,
    slotAssetsInlineDetailsSlotId,
  ]);
  const isPreFlowPlayoutExpanded = useMemo(
    () => flowControlInlineMedia && flowControlInlineMediaSlotId === FLOW_CONTROL_PRE_FLOW_PLAYOUT_ID,
    [flowControlInlineMedia, flowControlInlineMediaSlotId],
  );
  const isAfterFlowPlayoutExpanded = useMemo(
    () => flowControlInlineMedia && flowControlInlineMediaSlotId === FLOW_CONTROL_AFTER_FLOW_PLAYOUT_ID,
    [flowControlInlineMedia, flowControlInlineMediaSlotId],
  );
  const isStartRowFlowLevelDetailsSelected = useMemo(() => {
    if (startRowMediaOpenDetailsMode === 'flowSlotAssets') {
      if (!isSmallScreen) {
        return !selectedSlot && flowSlotAssetsWideAnchor !== 'end';
      }
      return (
        Boolean(middleSectionOpensOverlayOnSmallScreen) &&
        slotAssetsInlineDetailsSlotId === FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID
      );
    }
    if (startRowMediaOpenDetailsMode === 'flowControl') {
      return flowControlInlineMediaSlotId === FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID;
    }
    return false;
  }, [
    startRowMediaOpenDetailsMode,
    isSmallScreen,
    selectedSlot,
    middleSectionOpensOverlayOnSmallScreen,
    slotAssetsInlineDetailsSlotId,
    flowControlInlineMediaSlotId,
    flowSlotAssetsWideAnchor,
  ]);
  const isEndRowFlowControlMediaSelected = useMemo(() => {
    if (startRowMediaOpenDetailsMode !== 'flowControl') {
      return false;
    }
    return flowControlInlineMediaSlotId === FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID;
  }, [startRowMediaOpenDetailsMode, flowControlInlineMediaSlotId]);
  const [inlineScheduleDetailsTab, setInlineScheduleDetailsTab] = useState<ScheduleInlineDetailsTab>('session');
  // Slot Assets: parent passes `slotAssets` for both wide (two-column) and narrow (inline-under-row) layouts.
  // Item uses this to block selection/timing/title clicks for unpublished/deleted sessions — must not be cleared on wide screens.
  const smallScreenInlineDetailsVariantResolved =
    smallScreenInlineDetailsVariant === 'slotAssets'
      ? 'slotAssets'
      : middleSectionOpensOverlayOnSmallScreen && isSmallScreen
        ? (smallScreenInlineDetailsVariant ?? 'slotAssets')
        : undefined;
  const flowMode = useModeStore((s) => s.flowMode);

  const event = useEventStore((s) => s.event);
  const eventPlan = useEventStore((s) => s.eventPlan);
  const eventOrganize = useEventStore((s) => s.eventOrganize);
  const [searchParams] = useSearchParams();
  const eventSlotIdParam = searchParams.get('eventSlotId');

  const effectiveFlowPlanForPublish = useMemo(
    () => flowPlan ?? (flow?.mode === 'PLAN' ? flow : null),
    [flowPlan, flow],
  );
  const showPublishInInfoHeader = useMemo(
    () => !readOnly && Boolean(eventSlotIdParam) && Boolean(effectiveFlowPlanForPublish) && Boolean(flow),
    [readOnly, eventSlotIdParam, effectiveFlowPlanForPublish, flow],
  );

  const publishStateForInfoTitle = useFlowPublishStateForFlowDetail(effectiveFlowPlanForPublish ?? undefined);
  const collapsibleInfoTitle = useMemo(() => {
    if (!showPublishInInfoHeader || !publishStateForInfoTitle) {
      return t('flows.detail.slotList.info.title');
    }
    const key = slotListInfoTitleKeyFromPublishState(publishStateForInfoTitle);
    return t(`flows.detail.slotList.info.${key}`);
  }, [publishStateForInfoTitle, showPublishInInfoHeader, t]);

  const { socket, isConnected } = useSlotListSync({
    flowId: flowId!,
    mode,
    eventSlotIdParam,
    event,
    eventPlan,
    eventOrganize,
    queryClient,
    setFlow,
    setFlowPlan,
    setFlowOrganize,
    setIsSyncing,
    setIsHydrated,
  });
  const { emitFlowSync, canEmit } = useFlowSyncTransport();
  const revertLocalSlotsRef = useRef<(serverFlow?: FlowDto) => void>(() => {});

  // Set hydrated to true when flow data is available from React Query (not just websocket)
  // This ensures the component renders even if websocket hasn't sent initial data
  useEffect(() => {
    if (flow && flow.slots && !isHydrated) {
      queueMicrotask(() => setIsHydrated(true));
    }
  }, [flow, isHydrated]);

  const syncFlow = (flow: FlowDto, options?: { omitEventSlotTiming?: boolean }) => {
    if (!canEmit) return;

    setIsSyncing(true);

    const previousFlow = useFlowStore.getState().flowPlan ?? useFlowStore.getState().flow;
    const optimisticFlow = mergeFlowPreservingLiveContext(flow, previousFlow);
    setFlow(optimisticFlow);

    const payload = buildFlowSyncEmitPayload(optimisticFlow, eventSlotIdParam, options);

    const resolveFlowGetMode = () => {
      const resolved = (flowId && flowMode[flowId]) || (useFlowStore.getState().flowPlan ? 'PLAN' : 'ORGANIZE');
      return resolved === 'PERFORM' ? 'ORGANIZE' : resolved;
    };

    void (async () => {
      const { ok, flow: serverFlow } = await emitFlowSync(payload);
      if (ok && serverFlow && flowId) {
        const normalized = mergeFlowPreservingLiveContext(
          normalizeFlowForPlanView(serverFlow, flowId),
          useFlowStore.getState().flowPlan ?? useFlowStore.getState().flow,
        );
        setFlowPlan(normalized);
        const activeViewMode = (flowId && flowMode[flowId]) || mode;
        if (isEntityOfMode(activeViewMode as never, 'PLAN')) {
          setFlow(normalized);
        }
        queryClient.setQueryData([GET_FLOW_QUERY_KEY, flowId, eventSlotIdParam ?? undefined], normalized);
        setIsDragOptimistic(false);
        return;
      }
      if (!ok) {
        console.error('Flow sync failed');
        setIsDragOptimistic(false);
        revertLocalSlotsRef.current(useFlowStore.getState().flowPlan ?? useFlowStore.getState().flow ?? undefined);
        if (socket && isConnected) {
          socket.emit(
            'flowGet',
            {
              flowId,
              mode: resolveFlowGetMode(),
              eventSlotId: eventSlotIdParam || undefined,
            },
            (freshFlow: FlowDto) => {
              if (!freshFlow || !flowId) return;
              const normalized = mergeFlowPreservingLiveContext(
                normalizeFlowForPlanView(freshFlow, flowId),
                useFlowStore.getState().flowPlan ?? useFlowStore.getState().flow,
              );
              setFlowPlan(normalized);
              const activeViewMode = (flowId && flowMode[flowId]) || mode;
              if (isEntityOfMode(activeViewMode as never, 'PLAN')) {
                setFlow(normalized);
              }
              revertLocalSlotsRef.current(normalized);
            },
          );
        }
      }
    })();
  };

  useEffect(() => {
    if (isSyncing) {
      const timer = setTimeout(() => {
        setIsSyncing(false);
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [isSyncing]);

  const [receivedLocalSlots, setReceivedLocalSlots] = useState<FlowSlotDto[] | undefined>(undefined);
  const [localSlots, setLocalSlots] = useState<FlowSlotDto[]>([]);
  const [previewFlow, setPreviewFlow] = useState<FlowDto | undefined>(undefined);
  const [refreshing, setRefreshing] = useState(false);
  const {
    editingTiming,
    setEditingTiming,
    editingSlotId,
    setEditingSlotId,
    editingEditSlotId,
    expandedSlotId,
    expandedFormType,
    handleExpandedChange,
    handleFormTypeChange,
    isEditingEndLabel,
    setIsEditingEndLabel,
    isEditingEndTiming,
    setIsEditingEndTiming,
  } = useSlotListState(mode);
  // Initialize currentFlowId to flowId to prevent blocking initial render
  const [currentFlowId, setCurrentFlowId] = useState<string | undefined>(flowId);
  const [isDragOptimistic, setIsDragOptimistic] = useState(false);
  const [, setIsDragging] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const revertLocalSlotsFromServerFlow = useCallback(
    (serverFlow?: FlowDto) => {
      if (!serverFlow?.slots) {
        return;
      }
      setLocalSlots(buildLocalSlotsFromFlow(serverFlow));
      setPreviewFlow(undefined);
      setIsDragOptimistic(false);
    },
    [setLocalSlots, setPreviewFlow, setIsDragOptimistic],
  );
  useEffect(() => {
    revertLocalSlotsRef.current = revertLocalSlotsFromServerFlow;
  }, [revertLocalSlotsFromServerFlow]);
  const [dragLiveSlotLock, setDragLiveSlotLock] = useState<ScheduleLiveTimedSlotLock | null>(null);
  // Use flowToUse (flowOrganize when modeOverride ORGANIZE) and previewFlow - eventSlot should only be a target, not a source
  const flowForTiming = flowToUse;
  const previewFlowForTiming = previewFlow;

  /** Plan slot id from URL → organize event slot is `slot.origin === eventSlotId` (not organize `slot.id`). */
  const startItemSlot = useMemo(() => {
    if (!eventSlotIdParam) return undefined;
    if (mode === 'PLAN') {
      return (
        previewFlowForTiming?.slot ??
        eventPlan?.slots?.find((slot) => slot.id === eventSlotIdParam || slot.origin === eventSlotIdParam) ??
        flowPlan?.slot
      );
    }
    if (isOrganizeMode(mode)) {
      const slots = eventOrganize?.slots;
      if (slots?.length) {
        const byOrigin = slots.find((slot) => slot.origin === eventSlotIdParam);
        if (byOrigin) return byOrigin;
        const byId = slots.find((slot) => slot.id === eventSlotIdParam);
        if (byId) return byId;
      }
      return flowOrganize?.slot;
    }
    return undefined;
  }, [eventSlotIdParam, eventOrganize, eventPlan, flowOrganize, flowPlan, mode, previewFlowForTiming]);

  const flowStartOffset = useMemo(() => {
    const baseFlow = previewFlowForTiming ?? flowForTiming;
    if (eventSlotIdParam && startItemSlot) {
      return startItemSlot.startOffsetMinutes ?? 0;
    }
    return baseFlow?.slot?.startOffsetMinutes ?? 0;
  }, [eventSlotIdParam, startItemSlot, previewFlowForTiming, flowForTiming]);
  const minuteBeforeMidnight = 24 * 60 - 1;

  const {
    formOffset,
    formDuration,
    formAvailableDuration,
    formSlotType,
    formInsertBeforeSlotId,
    formInsertAfterSlotId,
    isUntimedFormActive,
    isFormActive,
    handleFormClose,
    handleFormSuccess,
    handleFormDurationChange,
    handleShowForm,
  } = useSlotListForm({
    flow,
    previewFlow: previewFlowForTiming,
    localSlots,
    isEditingEndTiming,
    editingTiming,
    minuteBeforeMidnight,
  });

  // Helper function to clear all slot list state
  const clearSlotListState = useCallback(() => {
    setReceivedLocalSlots(undefined);
    setLocalSlots([]);
    setSelectedSlot(undefined);
    setRefreshing(false);
    setEditingTiming(false);
    setEditingSlotId(null);
    setIsDragOptimistic(false);
  }, [
    setEditingSlotId,
    setEditingTiming,
    setIsDragOptimistic,
    setLocalSlots,
    setReceivedLocalSlots,
    setRefreshing,
    setSelectedSlot,
  ]);

  // Detect flow ID changes - use useEffect to avoid clearing flow during render
  useEffect(() => {
    if (flowId !== currentFlowId) {
      queueMicrotask(() => {
        setCurrentFlowId(flowId);
        clearSlotListState();
        // Only clear flow if we're switching to a different flowId (not on initial mount)
        // On initial mount, let FlowDetail's useEffect populate it from React Query
        if (currentFlowId !== undefined) {
          setFlow(undefined);
        }
      });
    }
  }, [flowId, currentFlowId, clearSlotListState, setFlow]);

  const {
    calculationSlots,
    timedSlots,
    untimedSlots,
    timedSlotsForDnd,
    untimedSlotsForDnd,
    timedSortableIds,
    untimedSortableIds,
    flowDuration,
    flowEndOffset,
  } = useSlotListComputed({
    localSlots,
    formState: {
      formInsertBeforeSlotId,
      formInsertAfterSlotId,
      formOffset,
      formDuration,
      formSlotType,
      isUntimedFormActive,
      isFormActive,
    },
    previewFlow: previewFlowForTiming,
    flow: flowForTiming,
    flowStartOffset,
    mode,
  });
  const canonicalTimedSlots = useMemo(() => {
    const source = flowPlan ?? flowToUse;
    if (!source?.slots?.length) {
      return [];
    }
    return source.slots.filter((slot) => slot.contentType !== 'Gap' && !isUntimedSlot(slot));
  }, [flowPlan, flowToUse]);
  const liveSlotLock = useScheduleLiveSlotLock(canonicalTimedSlots, showSlotDragHandle && !readOnly);
  const scheduleLiveTimedSlotLock = activeId && dragLiveSlotLock ? dragLiveSlotLock : liveSlotLock;
  const TIMED_CONTAINER_ID = 'container-timed';
  const UNTIMED_CONTAINER_ID = 'container-untimed';
  const { sensors, onDragStart, onDragOver, onDragEnd, onDragCancel, dragTimedIds, dragUntimedIds } = useSlotListDnd({
    timedSlots,
    untimedSlots,
    timedSortableIds,
    untimedSortableIds,
    flow,
    syncFlow,
    setLocalSlots,
    setRefreshing,
    setIsDragOptimistic,
    setIsDragging,
    setActiveId,
    TIMED_CONTAINER_ID,
    UNTIMED_CONTAINER_ID,
    scheduleLiveTimedSlotLock,
  });

  const activeSlot = useMemo(
    () => (activeId ? [...timedSlotsForDnd, ...untimedSlotsForDnd].find((slot) => slot.id === activeId) : null),
    [activeId, timedSlotsForDnd, untimedSlotsForDnd],
  );
  const timedSortableIdsForRender = dragTimedIds ?? timedSortableIds;
  const untimedSortableIdsForRender = dragUntimedIds ?? untimedSortableIds;
  const lastOverIdRef = useRef<UniqueIdentifier | null>(null);
  const timedIdsRef = useRef<UniqueIdentifier[]>([]);
  const untimedIdsRef = useRef<UniqueIdentifier[]>([]);
  useEffect(() => {
    timedIdsRef.current = timedSortableIdsForRender;
  }, [timedSortableIdsForRender]);
  useEffect(() => {
    untimedIdsRef.current = untimedSortableIdsForRender;
  }, [untimedSortableIdsForRender]);
  const collisionDetectionStrategy = useCallback<CollisionDetection>(
    (args) => {
      const pointerCollisions = pointerWithin(args);
      const baseCollisions = pointerCollisions.length > 0 ? pointerCollisions : rectIntersection(args);
      let overId = baseCollisions[0]?.id ?? null;

      const timedItems = timedIdsRef.current;
      const untimedItems = untimedIdsRef.current;
      const resolveContainerId = (id: UniqueIdentifier | null) => {
        if (id === TIMED_CONTAINER_ID || id === UNTIMED_CONTAINER_ID) return id;
        if (timedItems.includes(id as string)) return TIMED_CONTAINER_ID;
        if (untimedItems.includes(id as string)) return UNTIMED_CONTAINER_ID;
        return null;
      };

      const containerId = resolveContainerId(overId);
      if (containerId) {
        const items = containerId === TIMED_CONTAINER_ID ? timedItems : untimedItems;
        const itemContainers = args.droppableContainers.filter((container) => items.includes(container.id as string));
        if (itemContainers.length > 0) {
          const itemCollisions = closestCenter({ ...args, droppableContainers: itemContainers });
          if (itemCollisions[0]?.id) {
            overId = itemCollisions[0].id;
          }
        }

        const pointerY = args.pointerCoordinates?.y;
        if (pointerY !== undefined && items.length > 0) {
          const lastItemId = items[items.length - 1];
          const lastRect = args.droppableRects.get(lastItemId);
          if (lastRect && pointerY > lastRect.bottom) {
            overId = containerId;
          }
        }
      }

      if (overId != null) {
        lastOverIdRef.current = overId;
        return [{ id: overId }];
      }

      return lastOverIdRef.current ? [{ id: lastOverIdRef.current }] : [];
    },
    [TIMED_CONTAINER_ID, UNTIMED_CONTAINER_ID],
  );
  const slotsByIdForRender = useMemo(() => {
    const entries = [...timedSlotsForDnd, ...untimedSlots].map((slot) => [slot.id, slot] as const);
    return new Map(entries);
  }, [timedSlotsForDnd, untimedSlots]);
  const timedSlotsForRender = useMemo(() => {
    if (!dragTimedIds) return timedSlots;
    const orderedTimedSlots = dragTimedIds
      .map((id) => slotsByIdForRender.get(id))
      .filter((slot): slot is FlowSlotDto => Boolean(slot));
    return orderedTimedSlots.length > 0 ? orderedTimedSlots : timedSlots;
  }, [dragTimedIds, timedSlots, slotsByIdForRender]);
  const untimedSlotsForRender = useMemo(() => {
    if (!dragUntimedIds) return untimedSlots;
    return dragUntimedIds.map((id) => slotsByIdForRender.get(id)).filter((slot): slot is FlowSlotDto => Boolean(slot));
  }, [dragUntimedIds, untimedSlots, slotsByIdForRender]);
  const previewTimedCalculationSlots = useMemo(() => {
    const baseSlots = (() => {
      if (isFormActive && formDuration > 0) return calculationSlots;
      if (!dragTimedIds) return timedSlotsForDnd;
      return dragTimedIds
        .map((id) => {
          const slot = slotsByIdForRender.get(id);
          if (!slot) return null;
          if (id === activeId && activeSlot && isUntimedSlot(activeSlot)) {
            return {
              ...slot,
              isUntimed: false,
              plannedOffsetMinutes: undefined,
              computedOffsetMinutes: undefined,
              plannedDurationMinutes:
                slot.plannedDurationMinutes ??
                slot.content?.plannedDurationMinutes ??
                slot.computedDurationMinutes ??
                0,
            };
          }
          return slot;
        })
        .filter((slot): slot is FlowSlotDto => Boolean(slot));
    })();
    const timedOnly = baseSlots.filter((slot) => slot.id.startsWith('temp-') || !isUntimedSlot(slot));
    const durationToUse = (() => {
      const flowToUse = previewFlowForTiming ?? flowForTiming;
      const fixedDuration = flowToUse?.durationPlannedMinutes;
      return fixedDuration !== undefined && fixedDuration > 0 ? fixedDuration : undefined;
    })();
    return computeAllSlotTimings(timedOnly, durationToUse);
  }, [
    isFormActive,
    formDuration,
    calculationSlots,
    dragTimedIds,
    timedSlotsForDnd,
    slotsByIdForRender,
    activeId,
    activeSlot,
    previewFlowForTiming,
    flowForTiming,
  ]);
  const endGapBefore = useMemo(() => {
    const lastSlot = previewTimedCalculationSlots[previewTimedCalculationSlots.length - 1];
    const lastOffset = lastSlot?.computedOffsetMinutes ?? 0;
    const lastDuration = lastSlot?.computedDurationMinutes ?? 0;
    const startOffset = lastOffset + lastDuration;
    const flowToUse = previewFlowForTiming ?? flowForTiming;
    const hasFixedEnd = flowToUse?.durationPlannedMinutes !== undefined && flowToUse.durationPlannedMinutes > 0;
    if (!hasFixedEnd) {
      return { startOffset, duration: 0 };
    }
    const available = Math.max(0, (flowToUse?.durationPlannedMinutes ?? 0) - startOffset);
    return { startOffset, duration: available };
  }, [previewTimedCalculationSlots, previewFlowForTiming, flowForTiming]);
  const endGapInsertAfterSlotId = useMemo(
    () => previewTimedCalculationSlots[previewTimedCalculationSlots.length - 1]?.id,
    [previewTimedCalculationSlots],
  );
  const flowForEndGap = previewFlowForTiming ?? flowForTiming;
  const hasFixedEndTime =
    flowForEndGap?.durationPlannedMinutes !== undefined && flowForEndGap.durationPlannedMinutes > 0;
  /** Hide the gap row before "End of Flow" when there is no time left (schedule tab + fixed end / organize). */
  const shouldShowEndGap = endGapBefore.duration > 0 && (!isOrganizeMode(mode) || hasFixedEndTime);
  /** End-gap row already exposes NEW/BREAK; hide duplicates in the empty timed section. */
  const suppressEmptyTimedWizardBreak = hasFixedEndTime && shouldShowEndGap;

  useEffect(() => {
    if (isDragOptimistic) {
      return;
    }
    if (flowToUse && flowToUse.slots) {
      const timedSlots = flowToUse.slots.filter((slot) => slot.contentType !== 'Gap' && !isUntimedSlot(slot));
      const untimedSlots = flowToUse.slots.filter((slot) => slot.contentType !== 'Gap' && isUntimedSlot(slot));
      const flowDuration = flowToUse.durationPlannedMinutes ?? calculateFlowDurationFromSlots(timedSlots);
      const computedSlots = computeAllSlotTimings(timedSlots, flowDuration);
      const slotsToProcess = [...computedSlots, ...untimedSlots];

      // Clear preview flow when actual flow changes from server
      queueMicrotask(() => setPreviewFlow(undefined));
      queueMicrotask(() => setReceivedLocalSlots(slotsToProcess));

      if (selectedSlot) {
        const updatedSelectedSlot = findUpdatedSlotForSelection(slotsToProcess, selectedSlot);
        queueMicrotask(() => {
          if (updatedSelectedSlot) {
            setSelectedSlot(updatedSelectedSlot);
          } else {
            // If selected slot is not found in new flow, clear selection
            setSelectedSlot(undefined);
          }
        });
      }
    } else if (!flowToUse) {
      // If flow is cleared, reset local state - defer with queueMicrotask
      queueMicrotask(() => {
        setPreviewFlow(undefined);
        setReceivedLocalSlots(undefined);
        setLocalSlots([]);
        setIsDragOptimistic(false);
      });
    }
  }, [flowToUse?.id, flowToUse?.version, flowToUse?.slots, isDragOptimistic, setSelectedSlot]);
  useEffect(() => {
    if (receivedLocalSlots && !editingTiming && !isDragOptimistic) {
      queueMicrotask(() => {
        setLocalSlots(receivedLocalSlots);
        setReceivedLocalSlots(undefined);
      });
    }
  }, [receivedLocalSlots, editingTiming, isDragOptimistic]);

  const startItemFlow = useMemo(() => {
    if (!eventSlotIdParam) return flow;
    if (mode === 'PLAN') {
      const flowBase = previewFlowForTiming ?? flowPlan;
      if (!flowBase) return undefined;
      return startItemSlot ? { ...flowBase, slot: startItemSlot } : flowBase;
    }
    if (isOrganizeMode(mode)) {
      if (!flowOrganize) return undefined;
      return startItemSlot ? { ...flowOrganize, slot: startItemSlot } : flowOrganize;
    }
    return flow;
  }, [eventSlotIdParam, flow, flowOrganize, flowPlan, mode, startItemSlot]);

  const startItemEvent = useMemo(() => {
    if (!eventSlotIdParam) return event;
    if (mode === 'PLAN') return eventPlan;
    if (isOrganizeMode(mode)) return eventOrganize;
    return undefined;
  }, [event, eventOrganize, eventPlan, eventSlotIdParam, mode]);

  // For master flows (no eventSlotIdParam), don't show start time
  const startTime = useMemo(() => {
    if (!eventSlotIdParam) return undefined; // Master flow - no start time
    return minuteOffsetToTime(startItemFlow?.slot?.startOffsetMinutes ?? 0);
  }, [eventSlotIdParam, startItemFlow]);
  // For display purposes: show start time when no slots and no fixed end-time, otherwise show calculated end time
  // When a create form is active, include the form's planned duration in the end-time calculation
  const displayFlowEndOffset = useMemo(() => {
    const flowToUse = previewFlowForTiming ?? flowForTiming;
    const fixedDuration = flowToUse?.durationPlannedMinutes;
    if (fixedDuration !== undefined && fixedDuration > 0) {
      return flowStartOffset + fixedDuration;
    }
    if (timedSlots.length === 0) {
      return flowStartOffset;
    }
    const lastSlot = previewTimedCalculationSlots[previewTimedCalculationSlots.length - 1];
    return flowStartOffset + (lastSlot?.computedOffsetMinutes ?? 0) + (lastSlot?.computedDurationMinutes ?? 0);
  }, [previewFlowForTiming, flowForTiming, flowStartOffset, timedSlots, previewTimedCalculationSlots]);

  /** End row: when there are no timed rows yet, show `flowDuration ?? 0` (0 = start + 0, or preview from active timed create form). */
  const endRowFlowDuration = useMemo(() => {
    const flowToUse = previewFlowForTiming ?? flowForTiming;
    const hasFixedEndTime = flowToUse?.durationPlannedMinutes !== undefined && flowToUse.durationPlannedMinutes > 0;
    if (hasFixedEndTime) return flowDuration;
    if (timedSlots.length === 0) return flowDuration ?? 0;
    return flowDuration;
  }, [previewFlowForTiming, flowForTiming, flowDuration, timedSlots.length]);

  const endTime = useMemo(() => {
    // Check if there's a fixed end-time
    const flowToUse = previewFlowForTiming ?? flowForTiming;
    const hasFixedEndTime = flowToUse?.durationPlannedMinutes !== undefined && flowToUse.durationPlannedMinutes > 0;

    // If there's a fixed end-time, use flowEndOffset
    if (hasFixedEndTime) {
      return minuteOffsetToTime(displayFlowEndOffset);
    }

    // If no fixed end-time, use flowEndOffset which is calculated from the last slot's end time
    // This includes all slots including those after the new slot form
    if (displayFlowEndOffset !== undefined) {
      return minuteOffsetToTime(displayFlowEndOffset);
    }

    // Fallback to start time if no slots
    return minuteOffsetToTime(flowStartOffset);
  }, [displayFlowEndOffset, flowStartOffset, previewFlowForTiming, flowForTiming]);

  const cancelChanges = useCallback(() => {
    // Clear preview flow to revert to actual flow state
    setPreviewFlow(undefined);

    if (flowToUse?.slots) {
      const timedSlots = flowToUse.slots.filter((slot) => slot.contentType !== 'Gap' && !isUntimedSlot(slot));
      const untimedSlots = flowToUse.slots.filter((slot) => slot.contentType !== 'Gap' && isUntimedSlot(slot));
      const durationToUse = flowToUse.durationPlannedMinutes ?? calculateFlowDurationFromSlots(timedSlots);
      const computedSlots = computeAllSlotTimings(timedSlots, durationToUse);
      setLocalSlots([...computedSlots, ...untimedSlots]);
    }
    setEditingSlotId(null);
    setIsEditingEndTiming(false);
  }, [flowToUse, setEditingSlotId, setIsEditingEndTiming, setLocalSlots, setPreviewFlow]);

  // Handle editing timing state changes
  const handleSetEditingTiming = useCallback(
    (editing: boolean, slotId?: string) => {
      // Close end-timing form if opening a slot timing form
      if (editing && slotId && isEditingEndTiming) {
        setPreviewFlow(undefined);
        setIsEditingEndTiming(false);
      }

      if (editing && slotId) {
        setSlotAssetsInlineDetailsSlotId(null);
        // When opening timing form: set form type FIRST, then editing state (prevents flickering)
        handleFormTypeChange(slotId, 'timing');
        setEditingSlotId(slotId);
        setEditingTiming(editing);
        // Clear previous selection first
        if (onSlotSelect) {
          // Pass undefined to clear selection, but we need to check if onSlotSelect handles undefined
          // Since onSlotSelect expects a slot, we'll clear via setSelectedSlot instead
          setSelectedSlot(undefined);
        } else {
          setSelectedSlot(undefined);
        }
        const slotToSelect = localSlots.find((s) => s.id === slotId);
        if (slotToSelect) {
          if (isSmallScreen) {
            // On small screens, only scroll to slot (don't select, as it would open overlay)
            requestAnimationFrame(() => {
              const el = document.querySelector(`[data-slot-id="${slotId}"]`);
              if (!el) {
                // Try alternative selectors
                const selectors = [
                  `[data-flow-slot-id="${slotToSelect.contentType === 'Session' ? slotToSelect.content?.id : slotId}"]`,
                  `#flow-slot-${slotToSelect.contentType === 'Session' ? slotToSelect.content?.id : slotId}`,
                  `#slot-${slotId}`,
                ];
                const foundEl = selectors
                  .map((sel) => document.querySelector(sel))
                  .find((node): node is Element => !!node);
                if (foundEl && 'scrollIntoView' in foundEl) {
                  foundEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              } else if ('scrollIntoView' in el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            });
          } else {
            // On big screens, select the slot but mark as not user-initiated to prevent overlay
            if (onSlotSelect) {
              onSlotSelect(slotToSelect, false);
            } else {
              setSelectedSlot(slotToSelect);
            }
          }
        }
      } else {
        setEditingSlotId(null);
      }
    },
    [
      handleFormTypeChange,
      isEditingEndTiming,
      isSmallScreen,
      localSlots,
      onSlotSelect,
      setEditingSlotId,
      setEditingTiming,
      setIsEditingEndTiming,
      setPreviewFlow,
      setSelectedSlot,
    ],
  );

  const effectiveHandleShowForm = readOnly ? noop : handleShowForm;
  const effectiveHandleSetEditingTiming = readOnly ? noop : handleSetEditingTiming;

  /** Collapsing the slot row while timing is open must discard preview like the inline Cancel button. */
  const effectiveHandleExpandedChange = useCallback(
    (slotId: string | null) => {
      if (readOnly) return;
      if (slotId === null && expandedFormType === 'timing' && editingTiming) {
        cancelChanges();
      }
      handleExpandedChange(slotId);
    },
    [readOnly, handleExpandedChange, expandedFormType, editingTiming, cancelChanges],
  );

  /** Collapse expanded rows / narrow inline details when a drag starts so long-press reorder works. */
  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      setDragLiveSlotLock(liveSlotLock);
      if (expandedSlotId) {
        effectiveHandleExpandedChange(null);
      }
      if (isSmallScreen && slotAssetsInlineDetailsSlotId) {
        setSlotAssetsInlineDetailsSlotId(null);
      }
      if (flowControlInlineMediaSlotId && !flowControlInlineMedia) {
        useFlowControlMediaExpandStore.getState().setExpandId(null);
      }
      onDragStart(event);
    },
    [
      liveSlotLock,
      expandedSlotId,
      effectiveHandleExpandedChange,
      onDragStart,
      isSmallScreen,
      slotAssetsInlineDetailsSlotId,
      flowControlInlineMediaSlotId,
      flowControlInlineMedia,
    ],
  );

  const handleDragCancel = useCallback(() => {
    setDragLiveSlotLock(null);
    onDragCancel();
  }, [onDragCancel]);

  const handleDragEnd = useCallback(
    (event: Parameters<typeof onDragEnd>[0]) => {
      onDragEnd(event);
      setDragLiveSlotLock(null);
    },
    [onDragEnd],
  );

  const effectiveHandleFormTypeChange = useCallback(
    (slotId: string, formType: 'timing' | 'new' | null) => {
      // Single batch with handleFormTypeChange: avoid capture-phase closeInlineOnly racing layout
      // (ghost hit-area where the previous slot’s inline panel / expansion was).
      // Read-only lists never mount the edit form; clearing here would reset state before toggle() and block closing the inline panel.
      if (formType != null && !(readOnly && formType === 'new')) {
        setSlotAssetsInlineDetailsSlotId(null);
        if (!flowControlInlineMedia) {
          useFlowControlMediaExpandStore.getState().setExpandId(null);
        }
      }
      if (flowControlInlinePerform && formType) {
        onFlowControlPerformExpand?.(null);
      }
      if (readOnly) return;

      // Discard unsaved timing preview when closing timing, switching to edit form, or opening timing on another slot
      if (formType === null && expandedFormType === 'timing' && editingTiming) {
        cancelChanges();
      } else if (formType === 'new' && expandedFormType === 'timing' && editingTiming) {
        cancelChanges();
      } else if (formType === 'timing' && editingTiming && editingSlotId != null && editingSlotId !== slotId) {
        cancelChanges();
      }

      handleFormTypeChange(slotId, formType);
    },
    [
      flowControlInlinePerform,
      flowControlInlineMedia,
      readOnly,
      handleFormTypeChange,
      onFlowControlPerformExpand,
      expandedFormType,
      editingTiming,
      editingSlotId,
      cancelChanges,
    ],
  );

  /** Leaving the schedule "session" tab while inline timing is open should reset like Cancel. */
  const handleInlineScheduleDetailsTabChange = useCallback(
    (tab: ScheduleInlineDetailsTab) => {
      if (tab !== 'session' && expandedFormType === 'timing' && editingTiming && editingSlotId) {
        cancelChanges();
        handleFormTypeChange(editingSlotId, null);
      }
      setInlineScheduleDetailsTab(tab);
    },
    [expandedFormType, editingTiming, editingSlotId, cancelChanges, handleFormTypeChange],
  );

  const validateAndSwapSlotPosition = useCallback((slots: FlowSlotDto[], updatedSlot: FlowSlotDto): FlowSlotDto[] => {
    // If no fixed start-time, no reordering needed - array order is already correct
    if (updatedSlot.plannedOffsetMinutes === undefined || isUntimedSlot(updatedSlot)) return slots;

    const currentIndex = slots.findIndex((s) => s.id === updatedSlot.id);
    if (currentIndex === -1) return slots;

    // Find the correct position based on the fixed start-time
    // Compare against other slots' plannedOffsetMinutes (fixed start-times), not computed offsets
    const fixedStartTime = updatedSlot.plannedOffsetMinutes;

    // Find where this slot should be positioned based on its fixed start-time
    let targetIndex = currentIndex;

    // Check if slot should move forward (to later position in array)
    for (let i = currentIndex + 1; i < slots.length; i++) {
      const nextSlot = slots[i];

      // Skip untimed slots - they don't affect positioning
      if (isUntimedSlot(nextSlot)) {
        break;
      }

      // Compare fixed start-times: use plannedOffsetMinutes if available, otherwise skip
      const nextStart = nextSlot.plannedOffsetMinutes;
      if (nextStart === undefined) {
        continue; // Skip slots without fixed start-time for comparison
      }

      // If fixed start-time is greater than next slot's fixed start-time, move forward
      if (fixedStartTime > nextStart) {
        targetIndex = i;
      } else {
        break; // Found correct position
      }
    }

    // Check if slot should move backward (to earlier position in array)
    for (let i = currentIndex - 1; i >= 0; i--) {
      const prevSlot = slots[i];

      // Skip untimed slots - they don't affect positioning
      if (isUntimedSlot(prevSlot)) {
        break;
      }

      // Compare fixed start-times: use plannedOffsetMinutes if available, otherwise skip
      const prevStart = prevSlot.plannedOffsetMinutes;
      if (prevStart === undefined) {
        continue; // Skip slots without fixed start-time for comparison
      }

      // If fixed start-time is less than previous slot's fixed start-time, move backward
      if (fixedStartTime < prevStart) {
        targetIndex = i;
      } else {
        break; // Found correct position
      }
    }

    // Reorder array if position changed
    if (targetIndex !== currentIndex) {
      return arrayMove(slots, currentIndex, targetIndex);
    }

    return slots;
  }, []);

  const handlePreviewSlotUpdate = useCallback(
    (updatedSlot: FlowSlotDto) => {
      const appliedSlots = localSlots.map((s) => (s.id === updatedSlot.id ? { ...s, ...updatedSlot } : s));
      const validatedSlots = validateAndSwapSlotPosition(appliedSlots, updatedSlot);
      // Use flow.durationPlannedMinutes as primary source (flow is source of truth)
      const timedSlotsForDuration = validatedSlots.filter((slot) => slot.contentType !== 'Gap' && !isUntimedSlot(slot));
      const durationToUse = flow?.durationPlannedMinutes ?? calculateFlowDurationFromSlots(timedSlotsForDuration);
      const computedSlots = computeAllSlotTimings(validatedSlots, durationToUse);
      // computeAllSlotTimings already includes both timed and untimed slots, so use it directly
      setLocalSlots(computedSlots);
    },
    [localSlots, flow, setLocalSlots, validateAndSwapSlotPosition],
  );

  const handlePreviewFlowUpdate = useCallback(
    (updatedFlow: FlowDto) => {
      if (!flow) return;

      // Store preview flow in local state (don't update global flow store)
      setPreviewFlow(updatedFlow);

      // Recompute all slot timings with the new flow start-time and duration
      if (flow.slots) {
        const timedSlots = flow.slots.filter((slot) => slot.contentType !== 'Gap' && !isUntimedSlot(slot));
        const untimedSlots = flow.slots.filter((slot) => slot.contentType !== 'Gap' && isUntimedSlot(slot));
        const durationToUse = updatedFlow.durationPlannedMinutes ?? calculateFlowDurationFromSlots(timedSlots);
        const computedSlots = computeAllSlotTimings(timedSlots, durationToUse);
        setLocalSlots([...computedSlots, ...untimedSlots]);
      }
    },
    [flow, setLocalSlots, setPreviewFlow],
  );

  const handleFlowTimingSubmit = (updatedFlow: FlowDto) => {
    if (!flow) return;

    try {
      const timedSlots = (flow.slots ?? []).filter((slot) => slot.contentType !== 'Gap' && !isUntimedSlot(slot));
      const untimedSlots = (flow.slots ?? []).filter((slot) => slot.contentType !== 'Gap' && isUntimedSlot(slot));
      const durationToUse = updatedFlow.durationPlannedMinutes ?? calculateFlowDurationFromSlots(timedSlots);
      const computedSlots = computeAllSlotTimings(timedSlots, durationToUse).map((slot) => ({
        ...slot,
        plannedDurationMinutes: slot.computedDurationMinutes ?? slot.plannedDurationMinutes,
      }));
      const slotsToSync = [...computedSlots, ...untimedSlots];
      const flowToSync: FlowDto = { ...updatedFlow, slots: slotsToSync };

      setPreviewFlow(flowToSync);
      setLocalSlots(slotsToSync);
      syncFlow(flowToSync);

      notification.notifySuccess(
        t('flow.notification.updateSuccess', { title: flow.title ?? t('myEventsList.unTitled') }),
      );
    } catch {
      notification.notifyError(t('flow.notification.updateError'));
    }
  };

  const handleSlotUpdate = useCallback(
    (updatedSlot: FlowSlotDto) => {
      // Use original flow slots (with stored planned values) for position calculation
      // Compute timings from stored slots to get stored computed start times
      // This ensures we compare against stored computed values, not preview values
      const originalSlots = flow?.slots ?? localSlots;
      // Use flow.durationPlannedMinutes as primary source (flow is source of truth)
      const timedSlotsForDuration = originalSlots.filter((slot) => slot.contentType !== 'Gap' && !isUntimedSlot(slot));
      const durationToUse = flow?.durationPlannedMinutes ?? calculateFlowDurationFromSlots(timedSlotsForDuration);

      // Apply the update to original slots
      const appliedSlots = originalSlots.map((s) => (s.id === updatedSlot.id ? { ...s, ...updatedSlot } : s));

      // Reorder slot if fixed start-time was set/changed (compares against other slots' plannedOffsetMinutes)
      const validatedSlots = validateAndSwapSlotPosition(appliedSlots, updatedSlot);
      const timedSlots = validatedSlots.filter((slot) => slot.contentType !== 'Gap' && !isUntimedSlot(slot));
      const untimedSlots = validatedSlots.filter((slot) => slot.contentType !== 'Gap' && isUntimedSlot(slot));
      const computedSlots = computeAllSlotTimings(timedSlots, durationToUse);
      const allSlots = [...computedSlots, ...untimedSlots];

      setLocalSlots(allSlots);
      if (flow) syncFlow({ ...flow, slots: allSlots });
    },
    [localSlots, flow, setLocalSlots, syncFlow, validateAndSwapSlotPosition],
  );

  // Handler to close form
  // renderSlotsWithGaps function removed - gap slots are now rendered directly as slots

  // Handler for day change - must be before early returns to maintain hook order
  const handleDayChange = useCallback(
    (dayId: string) => {
      if (!flow || !event) return;
      const selectedDay = event.days.find((d) => d.id === dayId);
      if (!selectedDay) return;

      // Update flow with new day and sync immediately
      const updatedFlow: FlowDto = {
        ...flow,
        slot: flow.slot
          ? {
              ...flow.slot,
              day: selectedDay,
            }
          : undefined,
      };

      // Update local flow state immediately for optimistic update
      setFlow({
        ...updatedFlow,
        version: updatedFlow.version + 1,
      });

      // Sync to server
      syncFlow(updatedFlow);
    },
    [flow, event, syncFlow, setFlow],
  );

  // Handler for preview day change - must be before early returns to maintain hook order
  const handlePreviewDayChange = useCallback(
    (dayId: string) => {
      if (!flow || !event) return;
      const selectedDay = event.days.find((d) => d.id === dayId);
      if (!selectedDay) return;

      const updatedFlow: FlowDto = {
        ...flow,
        slot: flow.slot
          ? {
              ...flow.slot,
              day: selectedDay,
            }
          : undefined,
      };
      handlePreviewFlowUpdate(updatedFlow);
    },
    [flow, event, handlePreviewFlowUpdate],
  );

  const canRender = !!flow && (currentFlowId === undefined || currentFlowId === flowId);
  const hideSectionEmptyPlaceholderWhileDragging = Boolean(activeId);
  const showUntimedSlotListSection =
    mode !== 'PERFORM' && !(untimedSlotsForRender.length === 0 && (hideEmptyUntimedSection || isOrganizeMode(mode)));

  /** Publish + slot actions sit in a column to the right of CollapsibleInfoAlert, not inside its title row. */
  const slotListInfoAsideActions = useMemo(() => {
    if (readOnly || hideCollapsibleInfoAlert) return null;
    const showPublish = showPublishInInfoHeader && flow;
    const showPlanSlotActions = mode === 'PLAN' && showUntimedSlotListSection;
    if (!showPublish && !showPlanSlotActions) return null;
    return (
      <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
        {showPublish ? (
          <Publish
            flow={flow}
            buttonOrIcon="outlinedSmall"
            context="flowDetail"
            flowPlanOverride={effectiveFlowPlanForPublish ?? undefined}
            onIndicativeClick={onShowPublishAlert}
          />
        ) : null}
        {showPlanSlotActions ? (
          <Actions
            offset={0}
            duration={undefined}
            setSelectedSlot={setSelectedSlot}
            showBreak={false}
            addFromLibraryIsUntimed={true}
            addFromLibraryInsertBeforeSlotId={untimedSlotsForRender[0]?.id}
            onShowForm={(slotType) => {
              effectiveHandleShowForm('untimed-top', 0, slotType ?? 'Session', untimedSlotsForRender[0]?.id);
            }}
          />
        ) : null}
      </Stack>
    );
  }, [
    readOnly,
    hideCollapsibleInfoAlert,
    showPublishInInfoHeader,
    flow,
    effectiveFlowPlanForPublish,
    onShowPublishAlert,
    mode,
    showUntimedSlotListSection,
    untimedSlotsForRender,
    setSelectedSlot,
    effectiveHandleShowForm,
  ]);

  if (!canRender) return null;

  const infoPublicationRow =
    !readOnly && !hideCollapsibleInfoAlert ? (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <CollapsibleInfoAlert
            title={collapsibleInfoTitle}
            description={t('flows.detail.slotList.info.description')}
            sx={{ mt: 0 }}
            compact={compactInfoAlert}
            onExpandedChange={setSlotListInfoAlertExpanded}
          />
        </Box>
        {!slotListInfoAlertExpanded ? slotListInfoAsideActions : null}
      </Stack>
    ) : null;

  const slotListDndMainContentSx = {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    gap: flowControlInlineMedia ? FLOW_CONTROL_SLOT_LIST_GAP : 1,
  };

  const slotListDndMainContent = (
    <Box sx={slotListDndMainContentSx}>
      {!hideFlowStartSlotRow && !(hideFlowStartSlotRowWhenSmallScreen && isSmallScreen) && (
        <>
          {flowControlInlineMedia ? (
            <FlowControlPlayoutSegmentRow
              label={t('flows.detail.control.preFlowPlayout', { defaultValue: 'Pre-flow media playout' })}
              segmentAnchor="flow_start"
              segmentKind="pre_flow"
              segmentId={FLOW_CONTROL_PRE_FLOW_PLAYOUT_ID}
              isExpanded={isPreFlowPlayoutExpanded}
              onToggleExpand={() => handleFlowControlInlineMediaToggle(FLOW_CONTROL_PRE_FLOW_PLAYOUT_ID)}
            />
          ) : null}
          <Item
            flow={startItemFlow ?? flow}
            staticItem={{
              time: startTime || '',
              title: eventSlotIdParam
                ? undefined
                : mode === 'PLAN'
                  ? t('flows.detail.masterFlow.editMasterFlow')
                  : t('flows.detail.masterFlow.sharedMasterFlow'), // Master flow text or undefined for day selector
            }}
            selected={isStartRowFlowLevelDetailsSelected}
            onClick={() => {
              setSelectedSlot(undefined);
              if (
                startRowMediaOpenDetailsMode === 'flowSlotAssets' &&
                !isSmallScreen &&
                onFlowSlotAssetsWideAnchorChange
              ) {
                onFlowSlotAssetsWideAnchorChange('start');
                setSlotAssetsInlineDetailsSlotId(null);
              }
              if (flowControlInlinePerform || flowControlInlineMedia) {
                return;
              }
              setFlowDetailsDirection('up');
              setShowFlowDetails(startRowMediaOpenDetailsMode ?? 'flowSettings');
            }}
            setSelectedSlot={setSelectedSlot}
            startEndItem
            showStartItemFooter
            isStartItem
            {...(eventSlotIdParam &&
              !isOrganizeMode(mode) && {
                onStartTimeChange: (newStartTime: string) => {
                  if (!flow) return;
                  const newStartOffsetMinutes = dateToMinutesFromMidnight(newStartTime);

                  const updatedFlow: FlowDto = {
                    ...flow,
                    slot: flow.slot
                      ? {
                          ...flow.slot,
                          startOffsetMinutes: newStartOffsetMinutes,
                        }
                      : undefined,
                  };
                  syncFlow(updatedFlow);
                },
                onPreviewStartTimeChange: (newStartTime: string) => {
                  if (!flow) return;
                  const newStartOffsetMinutes = dateToMinutesFromMidnight(newStartTime);

                  const updatedFlow: FlowDto = {
                    ...flow,
                    slot: flow.slot
                      ? {
                          ...flow.slot,
                          startOffsetMinutes: newStartOffsetMinutes,
                        }
                      : undefined,
                  };
                  handlePreviewFlowUpdate(updatedFlow);
                },
                ...(startRowMediaOpenDetailsMode !== 'flowSlotAssets' &&
                  startRowMediaOpenDetailsMode !== 'flowControl' && {
                    onDayChange: handleDayChange,
                    onPreviewDayChange: handlePreviewDayChange,
                  }),
              })}
            previewFlow={previewFlow}
            onTimingSectionClick={() => {
              if (isOrganizeMode(mode)) {
                onShowTimingAlert?.();
              }
            }}
            onShowPublishAlert={onShowPublishAlert}
            event={startItemEvent}
            onShowFlowDetails={setShowFlowDetails}
            setFlowDetailsDirection={setFlowDetailsDirection}
            flowDuration={flowDuration}
            disableDrop={readOnly}
            rightSectionOnlyPublishIcon={rightSectionOnlyPublishIcon}
            modeOverride={modeOverride}
            startRowMediaOpenDetailsMode={startRowMediaOpenDetailsMode}
            flowControlInlinePerform={flowControlInlinePerform}
            flowControlPerformExpandedKey={flowControlPerformExpandedKey}
            onFlowControlPerformExpand={onFlowControlPerformExpand}
            onFlowControlStartItemMiddleClick={onFlowControlStartItemMiddleClick}
            isSmallScreen={isSmallScreen}
            middleSectionOpensOverlayOnSmallScreen={middleSectionOpensOverlayOnSmallScreen}
            onSlotAssetsInlineDetailsToggle={
              middleSectionOpensOverlayOnSmallScreen ? handleSlotAssetsInlineDetailsToggle : undefined
            }
            onSlotAssetsInlineDetailsClose={
              middleSectionOpensOverlayOnSmallScreen ? handleSlotAssetsInlineDetailsClose : undefined
            }
            slotInlineDetailsOpen={Boolean(
              middleSectionOpensOverlayOnSmallScreen &&
              isSmallScreen &&
              startRowMediaOpenDetailsMode === 'flowSlotAssets' &&
              slotAssetsInlineDetailsSlotId === FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID,
            )}
            onFlowStartSlotAssetsInlinePress={
              middleSectionOpensOverlayOnSmallScreen &&
              isSmallScreen &&
              startRowMediaOpenDetailsMode === 'flowSlotAssets'
                ? handleFlowStartSlotAssetsInlinePress
                : undefined
            }
            listFlowStartOffset={flowStartOffset}
            startItemInlineSlotAssetsRowId={
              (middleSectionOpensOverlayOnSmallScreen &&
                isSmallScreen &&
                startRowMediaOpenDetailsMode === 'flowSlotAssets') ||
              (flowControlInlineMedia && startRowMediaOpenDetailsMode === 'flowControl')
                ? FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID
                : undefined
            }
            flowControlInlineMedia={flowControlInlineMedia}
            onFlowControlInlineMediaToggle={flowControlInlineMedia ? handleFlowControlInlineMediaToggle : undefined}
            flowControlInlineMediaOpen={Boolean(
              flowControlInlineMedia && flowControlInlineMediaSlotId === FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID,
            )}
            rightSectionPlayIcon={rightSectionPlayIcon}
          />
          {flowControlInlineMedia && flowControlInlineMediaSlotId === FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID && (
            <FlowControlSlotMediaBelowRow slot={null} activeSlotId={flowControlInlineMediaSlotId} show />
          )}
          {middleSectionOpensOverlayOnSmallScreen &&
            isSmallScreen &&
            startRowMediaOpenDetailsMode === 'flowSlotAssets' &&
            slotAssetsInlineDetailsSlotId === FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID && (
              <Box
                sx={{
                  ...flowControlInlinePerformMiddleWrapperSx,
                  mx: 0,
                  bgcolor: 'background.paper',
                  px: 1,
                  py: 1.5,
                }}
                data-slot-inline-details
                data-slot-id={FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID}
              >
                <SlotAssetsDetails selectedSlot={undefined} slotTitle={flowToUse?.title ?? ''} listViewMode={mode} />
              </Box>
            )}
          {flowControlInlinePerform &&
            flowControlPerformExpandedKey === FLOW_CONTROL_PERFORM_EXPAND_FLOW &&
            renderFlowControlPerformMiddleFlow && (
              <Box sx={{ ...flowControlInlinePerformMiddleWrapperSx, mx: -1 }}>
                {renderFlowControlPerformMiddleFlow()}
              </Box>
            )}
        </>
      )}
      {showUntimedSlotListSection && (
        <SortableContext strategy={verticalListSortingStrategy} items={untimedSortableIdsForRender}>
          <UntimedSection
            mode={mode}
            refreshing={refreshing}
            t={t}
            isUntimedFormActive={isUntimedFormActive}
            formDuration={formDuration}
            formAvailableDuration={formAvailableDuration}
            formSlotType={formSlotType}
            formInsertBeforeSlotId={formInsertBeforeSlotId}
            handleFormSuccess={handleFormSuccess}
            handleFormClose={handleFormClose}
            handleFormDurationChange={handleFormDurationChange}
            handleShowForm={effectiveHandleShowForm}
            untimedSlots={untimedSlotsForRender}
            isSmallScreen={isSmallScreen}
            onSlotSelect={onSlotSelect}
            setSelectedSlot={setSelectedSlot}
            selectedSlot={selectedSlot}
            expandedSlotId={expandedSlotId}
            expandedFormType={expandedFormType}
            handleExpandedChange={effectiveHandleExpandedChange}
            handleFormTypeChange={effectiveHandleFormTypeChange}
            handleSlotUpdate={handleSlotUpdate}
            handlePreviewSlotUpdate={handlePreviewSlotUpdate}
            onShowTimingAlert={onShowTimingAlert}
            onShowPublishAlert={onShowPublishAlert}
            cancelChanges={cancelChanges}
            handleSetEditingTiming={effectiveHandleSetEditingTiming}
            editingSlotId={editingSlotId}
            editingEditSlotId={editingEditSlotId}
            previewFlow={previewFlow}
            flowEndOffset={flowEndOffset}
            setShowFlowDetails={setShowFlowDetails}
            setFlowDetailsDirection={setFlowDetailsDirection}
            containerId={UNTIMED_CONTAINER_ID}
            disableDrop={readOnly}
            modeOverride={modeOverride}
            rightSectionOnlyPublishIcon={rightSectionOnlyPublishIcon}
            middleSectionOpensOverlayOnSmallScreen={middleSectionOpensOverlayOnSmallScreen}
            startRowMediaOpenDetailsMode={startRowMediaOpenDetailsMode}
            onWideScreenSlotDeletedFromList={onWideScreenSlotDeletedFromList}
            onSessionPublishSuccess={onSessionPublishSuccess}
            flowControlInlinePerform={flowControlInlinePerform}
            flowControlPerformExpandedKey={flowControlPerformExpandedKey}
            onFlowControlPerformExpand={onFlowControlPerformExpand}
            renderFlowControlPerformMiddle={renderFlowControlPerformMiddle}
            slotAssetsInlineDetailsSlotId={isSmallScreen ? slotAssetsInlineDetailsSlotId : null}
            showSlotAssetsInlineDetails={Boolean(middleSectionOpensOverlayOnSmallScreen && isSmallScreen)}
            onSlotAssetsInlineDetailsToggle={
              middleSectionOpensOverlayOnSmallScreen ? handleSlotAssetsInlineDetailsToggle : undefined
            }
            onSlotAssetsInlineDetailsClose={
              middleSectionOpensOverlayOnSmallScreen ? handleSlotAssetsInlineDetailsClose : undefined
            }
            smallScreenInlineDetailsVariant={smallScreenInlineDetailsVariantResolved}
            inlineScheduleDetailsTab={
              smallScreenInlineDetailsVariantResolved === 'schedule' ? inlineScheduleDetailsTab : undefined
            }
            onInlineScheduleDetailsTabChange={
              smallScreenInlineDetailsVariantResolved === 'schedule' ? handleInlineScheduleDetailsTabChange : undefined
            }
            flowStartOffset={flowStartOffset}
            eventSlotIdParam={eventSlotIdParam}
            showSlotDragHandle={showSlotDragHandle}
            hideEmptyPlaceholderWhileDragging={hideSectionEmptyPlaceholderWhileDragging}
            flowControlInlineMedia={flowControlInlineMedia}
            flowControlInlineMediaSlotId={flowControlInlineMedia ? flowControlInlineMediaSlotId : null}
            flowControlPlayingExpandId={flowControlInlineMedia ? playingExpandId : null}
            onFlowControlInlineMediaToggle={flowControlInlineMedia ? handleFlowControlInlineMediaToggle : undefined}
            virtualInBetweenPlayout={flowControlInlineMedia ? virtualInBetweenPlayout : null}
            rightSectionPlayIcon={rightSectionPlayIcon}
          />
        </SortableContext>
      )}
      {showUntimedSlotListSection && !hideTimedUntimedSectionDivider && (
        <Box sx={{ py: 1, flexShrink: 0, width: '100%', boxSizing: 'border-box' }}>
          <Divider orientation="horizontal" sx={{ borderColor: 'slot.break.active', borderBottom: '1px solid' }} />
        </Box>
      )}
      <SortableContext strategy={verticalListSortingStrategy} items={timedSortableIdsForRender}>
        <TimedSection
          modeOverride={modeOverride}
          timedSlots={timedSlotsForRender}
          timedSlotsForDnd={timedSlotsForDnd}
          calculationSlots={calculationSlots}
          containerId={TIMED_CONTAINER_ID}
          isFormActive={isFormActive}
          formOffset={formOffset}
          formDuration={formDuration}
          formAvailableDuration={formAvailableDuration}
          formSlotType={formSlotType}
          formInsertBeforeSlotId={formInsertBeforeSlotId}
          formInsertAfterSlotId={formInsertAfterSlotId}
          handleFormSuccess={handleFormSuccess}
          handleFormClose={handleFormClose}
          handleFormDurationChange={handleFormDurationChange}
          handleShowForm={effectiveHandleShowForm}
          refreshing={refreshing}
          isHydrated={isHydrated}
          isSmallScreen={isSmallScreen}
          onSlotSelect={onSlotSelect}
          setSelectedSlot={setSelectedSlot}
          selectedSlot={selectedSlot}
          expandedSlotId={expandedSlotId}
          expandedFormType={expandedFormType}
          handleExpandedChange={effectiveHandleExpandedChange}
          handleFormTypeChange={effectiveHandleFormTypeChange}
          handleSlotUpdate={handleSlotUpdate}
          handlePreviewSlotUpdate={handlePreviewSlotUpdate}
          onShowTimingAlert={onShowTimingAlert}
          onShowPublishAlert={onShowPublishAlert}
          cancelChanges={cancelChanges}
          handleSetEditingTiming={effectiveHandleSetEditingTiming}
          editingSlotId={editingSlotId}
          editingEditSlotId={editingEditSlotId}
          previewFlow={previewFlow}
          flowEndOffset={flowEndOffset}
          setShowFlowDetails={setShowFlowDetails}
          setFlowDetailsDirection={setFlowDetailsDirection}
          flowStartOffset={flowStartOffset}
          dragTimedIds={dragTimedIds}
          minHeight={timedSlotsForRender.length === 0 ? 64 : undefined}
          hideEmptyPlaceholderWhileDragging={hideSectionEmptyPlaceholderWhileDragging}
          disableDrop={readOnly}
          rightSectionOnlyPublishIcon={rightSectionOnlyPublishIcon}
          middleSectionOpensOverlayOnSmallScreen={middleSectionOpensOverlayOnSmallScreen}
          startRowMediaOpenDetailsMode={startRowMediaOpenDetailsMode}
          onWideScreenSlotDeletedFromList={onWideScreenSlotDeletedFromList}
          onSessionPublishSuccess={onSessionPublishSuccess}
          flowControlInlinePerform={flowControlInlinePerform}
          flowControlPerformExpandedKey={flowControlPerformExpandedKey}
          onFlowControlPerformExpand={onFlowControlPerformExpand}
          renderFlowControlPerformMiddle={renderFlowControlPerformMiddle}
          slotAssetsInlineDetailsSlotId={isSmallScreen ? slotAssetsInlineDetailsSlotId : null}
          showSlotAssetsInlineDetails={Boolean(middleSectionOpensOverlayOnSmallScreen && isSmallScreen)}
          onSlotAssetsInlineDetailsToggle={
            middleSectionOpensOverlayOnSmallScreen ? handleSlotAssetsInlineDetailsToggle : undefined
          }
          onSlotAssetsInlineDetailsClose={
            middleSectionOpensOverlayOnSmallScreen ? handleSlotAssetsInlineDetailsClose : undefined
          }
          smallScreenInlineDetailsVariant={smallScreenInlineDetailsVariantResolved}
          inlineScheduleDetailsTab={
            smallScreenInlineDetailsVariantResolved === 'schedule' ? inlineScheduleDetailsTab : undefined
          }
          onInlineScheduleDetailsTabChange={
            smallScreenInlineDetailsVariantResolved === 'schedule' ? handleInlineScheduleDetailsTabChange : undefined
          }
          eventSlotIdParam={eventSlotIdParam}
          readOnly={readOnly}
          suppressEmptyTimedWizardBreak={suppressEmptyTimedWizardBreak}
          hideGapSlotWizardAndBreak={hideGapSlotWizardAndBreak}
          showSlotDragHandle={showSlotDragHandle}
          lockedTimedSlotIds={scheduleLiveTimedSlotLock.lockedTimedSlotIds}
          flowControlInlineMedia={flowControlInlineMedia}
          flowControlInlineMediaSlotId={flowControlInlineMedia ? flowControlInlineMediaSlotId : null}
          flowControlPlayingExpandId={flowControlInlineMedia ? playingExpandId : null}
          onFlowControlInlineMediaToggle={flowControlInlineMedia ? handleFlowControlInlineMediaToggle : undefined}
          virtualInBetweenPlayout={flowControlInlineMedia ? virtualInBetweenPlayout : null}
          rightSectionPlayIcon={rightSectionPlayIcon}
        />
        {isEditingEndLabel && flow ? (
          <InlineEndLabelForm
            flow={flow}
            onClose={() => setIsEditingEndLabel(false)}
            onCancel={() => setIsEditingEndLabel(false)}
          />
        ) : null}
      </SortableContext>
      {!isEditingEndLabel && (!isEditingEndTiming || mode === 'PLAN') && (
        <Item
          staticItem={{
            time: endTime,
            title: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}>
                <Box
                  component="span"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    maxWidth: '90%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {flow?.endOfFlowLabel || t('flows.detail.slotList.detail.endLabel')}
                </Box>
                {!isOrganizeMode(mode) && !readOnly ? (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditingEndLabel(true);
                    }}
                    sx={{
                      'padding': '2px',
                      'flexShrink': 0,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <EditOutlined fontSize="small" />
                  </IconButton>
                ) : null}
              </Box>
            ),
          }}
          onTimingSectionClick={() => {
            if (isOrganizeMode(mode)) {
              onShowTimingAlert?.();
              return;
            }
            if (readOnly) {
              return;
            }
            // Close any open slot timing form before opening end-timing form
            if (editingSlotId) {
              setPreviewFlow(undefined);
              setEditingSlotId(null);
              setEditingTiming(false);
            }
            // Close create form if open
            if (isFormActive) {
              handleFormClose();
            }
            setIsEditingEndTiming(true);
          }}
          locked={flow?.durationPlannedMinutes !== undefined && (flow.durationPlannedMinutes ?? 0) > 0}
          gapBefore={shouldShowEndGap ? endGapBefore : undefined}
          gapBeforeInsertAfterSlotId={endGapInsertAfterSlotId}
          forceGapBefore={shouldShowEndGap}
          hideGapTiming={!shouldShowEndGap || Boolean(!hasFixedEndTime)}
          hideGapStartTime={!shouldShowEndGap || endGapBefore.duration <= 0}
          customContent={
            !readOnly && isEditingEndTiming && flow ? (
              <InlineEndTimingForm
                flow={flow}
                previewFlow={previewFlow}
                localSlots={localSlots}
                eventSlotIdParam={eventSlotIdParam}
                onClose={() => {
                  setIsEditingEndTiming(false);
                  // Don't call cancelChanges here - it would close slot forms too
                  // Preview is already cleared above
                }}
                onPreviewUpdate={handlePreviewFlowUpdate}
                cancelChanges={cancelChanges}
                onSubmit={handleFlowTimingSubmit}
              />
            ) : undefined
          }
          setSelectedSlot={setSelectedSlot}
          onShowForm={effectiveHandleShowForm}
          startEndItem
          selected={isEndRowFlowLevelDetailsSelected || isEndRowFlowControlMediaSelected}
          flowDuration={endRowFlowDuration}
          disableDrop={readOnly}
          listFlowStartOffset={flowStartOffset}
          hideGapSlotWizardAndBreak={hideGapSlotWizardAndBreak}
          startRowMediaOpenDetailsMode={startRowMediaOpenDetailsMode}
          middleSectionOpensOverlayOnSmallScreen={middleSectionOpensOverlayOnSmallScreen}
          onFlowEndSlotAssetsInlinePress={
            startRowMediaOpenDetailsMode === 'flowSlotAssets' ? handleFlowEndSlotAssetsPress : undefined
          }
          slotInlineDetailsOpen={Boolean(
            middleSectionOpensOverlayOnSmallScreen &&
            isSmallScreen &&
            startRowMediaOpenDetailsMode === 'flowSlotAssets' &&
            slotAssetsInlineDetailsSlotId === FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID,
          )}
          endItemInlineSlotAssetsRowId={
            (middleSectionOpensOverlayOnSmallScreen &&
              isSmallScreen &&
              startRowMediaOpenDetailsMode === 'flowSlotAssets') ||
            (flowControlInlineMedia && startRowMediaOpenDetailsMode === 'flowControl')
              ? FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID
              : undefined
          }
          flowControlInlineMedia={flowControlInlineMedia}
          onFlowControlInlineMediaToggle={flowControlInlineMedia ? handleFlowControlInlineMediaToggle : undefined}
          flowControlInlineMediaOpen={Boolean(
            flowControlInlineMedia && flowControlInlineMediaSlotId === FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID,
          )}
          rightSectionPlayIcon={rightSectionPlayIcon}
          hideEndItemMediaIndicator={hideEndItemMediaIndicator}
          showSlotDragHandle={showSlotDragHandle}
        />
      )}
      {flowControlInlineMedia && flowControlInlineMediaSlotId === FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID && (
        <FlowControlSlotMediaBelowRow slot={null} activeSlotId={flowControlInlineMediaSlotId} show />
      )}
      {flowControlInlineMedia ? (
        <FlowControlPlayoutSegmentRow
          label={t('flows.detail.control.afterFlowPlayout', { defaultValue: 'After-flow media playout' })}
          segmentAnchor="flow_end"
          segmentKind="after_flow"
          segmentId={FLOW_CONTROL_AFTER_FLOW_PLAYOUT_ID}
          isExpanded={isAfterFlowPlayoutExpanded}
          onToggleExpand={() => handleFlowControlInlineMediaToggle(FLOW_CONTROL_AFTER_FLOW_PLAYOUT_ID)}
        />
      ) : null}
      {middleSectionOpensOverlayOnSmallScreen &&
        isSmallScreen &&
        startRowMediaOpenDetailsMode === 'flowSlotAssets' &&
        slotAssetsInlineDetailsSlotId === FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID &&
        flowToUse && (
          <Box
            sx={{
              ...flowControlInlinePerformMiddleWrapperSx,
              mx: 0,
              bgcolor: 'background.paper',
              px: 1,
              py: 1.5,
            }}
            data-slot-inline-details
            data-slot-id={FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID}
          >
            <FlowEndSlotAssetsDetails flow={flowToUse} />
          </Box>
        )}
    </Box>
  );

  const dndSlotListBody = (
    <DndContext
      onDragStart={handleDragStart}
      onDragOver={onDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy}
      modifiers={[restrictToVerticalAxis]}
    >
      {slotListDndMainContent}
      <DragOverlay>
        <DragOverlaySlot
          activeId={activeId}
          timedSlots={timedSlots}
          untimedSlots={untimedSlots}
          selectedSlotId={selectedSlot?.id}
          onSlotUpdate={handleSlotUpdate}
          onPreviewSlotUpdate={handlePreviewSlotUpdate}
          onShowTimingAlert={onShowTimingAlert}
          cancelChanges={cancelChanges}
          onSetEditingTiming={effectiveHandleSetEditingTiming}
          forceTimedPreview={Boolean(
            activeId && dragTimedIds?.includes(activeId) && activeSlot && isUntimedSlot(activeSlot),
          )}
          hideTime={Boolean(activeId && dragUntimedIds?.includes(activeId) && activeSlot && !isUntimedSlot(activeSlot))}
          rightSectionOnlyPublishIcon={rightSectionOnlyPublishIcon}
          listFlowStartOffset={flowStartOffset}
        />
      </DragOverlay>
    </DndContext>
  );

  const syncingToast = isSyncing ? (
    <Box
      sx={{
        'position': 'fixed',
        'bottom': 16,
        'right': 16,
        'zIndex': 1000,
        'borderRadius': '50%',
        'padding': 1,
        'animation': 'fadeInOut 700ms ease-in-out',
        '@keyframes fadeInOut': {
          '0%': {
            opacity: 0,
            transform: 'scale(0.8)',
          },
          '20%': {
            opacity: 1,
            transform: 'scale(1)',
          },
          '80%': {
            opacity: 1,
            transform: 'scale(1)',
          },
          '100%': {
            opacity: 0,
            transform: 'scale(0.8)',
          },
        },
      }}
    >
      <CheckCircleIcon color="success" sx={{ fontSize: 32 }} />
    </Box>
  ) : null;

  if (slotListBodyInScrollContainer) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          width: '100%',
        }}
      >
        {infoPublicationRow}
        <Box sx={slotListScrollAreaSx}>{dndSlotListBody}</Box>
        {syncingToast}
      </Box>
    );
  }

  return (
    <>
      {infoPublicationRow}
      {dndSlotListBody}
      {syncingToast}
    </>
  );
}
